/**
 * PageHeader - Reusable header component for list pages
 *
 * Provides consistent header layout with title, description, count, and action button.
 * The action button sits inline with the title (top-right); description and count appear below.
 *
 * @example
 * ```tsx
 * <PageHeader
 *   title="Seasons"
 *   description="Manage and view all seasons"
 *   count={filteredSeasons.length}
 *   countLabel="season"
 *   actionButton={
 *     <Button variant="primary" leftIcon={<HiPlus />} onClick={onOpen}>
 *       Add New Season
 *     </Button>
 *   }
 * />
 * ```
 */

"use client";

import { Box, Flex } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { Heading, Text } from "@/components";
import { useThemeTokens } from "@/lib/theme/tokens";
import { withPageHeaderActionStyles } from "./pageHeaderActionStyles";

export interface PageHeaderProps {
  /**
   * Page title
   */
  title: string;
  /**
   * Page description
   */
  description?: string;
  /**
   * Count of items (for display like "5 items found")
   */
  count?: number;
  /**
   * Label for count (singular form, will be pluralized automatically)
   */
  countLabel?: string;
  /**
   * Action button (typically "Add New X" button). On mobile, size and height
   * are adjusted automatically unless explicitly set on the button.
   */
  actionButton?: ReactNode;
  /**
   * Additional content to display below description
   */
  children?: ReactNode;
  /**
   * Additional margin bottom for the component
   */
  mb?: number;
}

export const PageHeader = ({
  title,
  description,
  count,
  countLabel,
  actionButton,
  children,
  mb,
}: PageHeaderProps) => {
  const theme = useThemeTokens();

  const getCountText = () => {
    if (count === undefined || countLabel === undefined) return null;
    
    const getPlural = (word: string) => {
      if (word.endsWith("y") && !/[aeiou]y$/i.test(word)) {
        return word.slice(0, -1) + "ies";
      }
      if (/(?:s|ss|sh|ch|x|z)$/i.test(word)) {
        return word + "es";
      }
      return word + "s";
    };
    
    return `${count} ${count === 1 ? countLabel : getPlural(countLabel)} found`;
  };

  return (
    <Box mb={mb ?? 6}>
      <Flex
        gap={3}
        align="center"
        justifyContent="space-between"
        mb={description || getCountText() || children ? 2 : 0}
      >
        <Heading
          flex={1}
          minW={0}
          fontSize={{ base: "2xl", md: "3xl" }}
          letterSpacing="-0.02em"
          fontWeight="600"
          color={theme.text.primary}
          lineHeight="1.2"
        >
          {title}
        </Heading>
        {actionButton ? (
          <Box flexShrink={0}>{withPageHeaderActionStyles(actionButton)}</Box>
        ) : null}
      </Flex>
      {description ? (
        <Text fontSize={{ base: "sm", md: "md" }} color={theme.text.tertiary} mb={2}>
          {description}
        </Text>
      ) : null}
      {getCountText() ? (
        <Text variant="secondary" fontSize="md">
          {getCountText()}
        </Text>
      ) : null}
      {children}
    </Box>
  );
};

